import { useEffect, useRef, useState } from "react";
import { Megaphone, Plus, Pencil, Trash2, X, ImagePlus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const EMPTY = { title: "", tagline: "", slogan: "", image: "", link: "", features: "", start_date: "", end_date: "", is_active: true };

const inputClass =
  "w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";

const AdForm = ({ initial, onClose, onSaved }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("Image trop lourde (max 2 Mo)"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pro/uploads", fd);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success(t("Affiche téléversée — recadrage automatique appliqué à l'affichage"));
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    const payload = {
      ...form,
      features: form.features.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3),
    };
    try {
      if (form.id) {
        await api.put(`/admin/ads/${form.id}`, payload);
        toast.success(t("Publicité mise à jour"));
      } else {
        await api.post("/admin/ads", payload);
        toast.success(t("Publicité créée"));
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" data-testid="ad-form-modal">
      <form onSubmit={submit} className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
            <Megaphone className="h-5 w-5 text-[#2563EB]" />
            {form.id ? t("Modifier la publicité") : t("Ajouter une publicité")}
          </h3>
          <button type="button" onClick={onClose} aria-label={t("Fermer")} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:text-slate-800">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 space-y-3.5">
          <input data-testid="ad-form-title" required value={form.title} onChange={set("title")} placeholder={t("Titre de la publicité")} className={inputClass} />
          <input data-testid="ad-form-tagline" value={form.tagline} onChange={set("tagline")} placeholder={t("Accroche (ex. : BAR • SPECTACLES • BONNE AMBIANCE)")} className={inputClass} />
          <input data-testid="ad-form-slogan" value={form.slogan} onChange={set("slogan")} placeholder={t("Slogan (ex. : Vos soirées prennent vie ici !)")} className={inputClass} />
          <input data-testid="ad-form-link" type="url" value={form.link} onChange={set("link")} placeholder={t("Lien de destination (https://…)")} className={inputClass} />

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-500">{t("Affiche (1200 × 400 px, ratio 3:1)")}</label>
            <div className="flex gap-2">
              <input data-testid="ad-form-image" value={form.image} onChange={set("image")} placeholder="https://…" className={inputClass} />
              <button
                type="button"
                data-testid="ad-form-upload"
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="flex shrink-0 items-center gap-1.5 rounded-2xl border border-slate-200 px-4 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB] disabled:opacity-60"
              >
                <ImagePlus className="h-4 w-4" />
                {uploading ? t("Envoi en cours…") : t("Téléverser")}
              </button>
            </div>
            <input ref={fileInput} type="file" accept="image/jpeg,image/png" className="hidden" onChange={uploadImage} />
            {form.image && (
              <div className="mt-2 overflow-hidden rounded-2xl border border-slate-100">
                <img src={form.image} alt={t("Aperçu")} className="h-28 w-full object-cover" />
              </div>
            )}
          </div>

          <input
            data-testid="ad-form-features"
            value={form.features}
            onChange={set("features")}
            placeholder={t("Caractéristiques (3 max, séparées par des virgules)")}
            className={inputClass}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">{t("Date de début")}</label>
              <input data-testid="ad-form-start" type="date" value={form.start_date} onChange={set("start_date")} className={inputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">{t("Date de fin")}</label>
              <input data-testid="ad-form-end" type="date" value={form.end_date} onChange={set("end_date")} className={inputClass} />
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-slate-700">
            <input data-testid="ad-form-active" type="checkbox" checked={form.is_active} onChange={set("is_active")} className="h-4 w-4 rounded border-slate-300 accent-[#2563EB]" />
            {t("Publicité active (visible sur l'accueil pendant la période)")}
          </label>
        </div>

        <div className="mt-6 flex gap-3">
          <button type="button" data-testid="ad-form-cancel" onClick={onClose} className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
            {t("Annuler")}
          </button>
          <button type="submit" data-testid="ad-form-submit" disabled={busy || !form.title.trim()} className="flex-1 rounded-full bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] disabled:opacity-60">
            {busy ? t("Enregistrement…") : form.id ? t("Enregistrer les modifications") : t("Ajouter la publicité")}
          </button>
        </div>
      </form>
    </div>
  );
};

export const AdminAds = () => {
  const { t, fmtDate } = useLanguage();
  const [ads, setAds] = useState(null);
  const [editing, setEditing] = useState(null); // null = fermé, {} = création, {…ad} = édition

  const load = () => api.get("/admin/ads").then((r) => setAds(r.data)).catch(() => setAds([]));

  useEffect(() => {
    load();
  }, []);

  const remove = async (ad) => {
    if (!window.confirm(t("Supprimer cette publicité ?"))) return;
    try {
      await api.delete(`/admin/ads/${ad.id}`);
      toast.success(t("Publicité supprimée"));
      load();
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    }
  };

  const toggle = async (ad) => {
    const next = !ad.is_active;
    try {
      await api.patch(`/admin/ads/${ad.id}/status`, { is_active: next });
      setAds((list) => list.map((a) => (a.id === ad.id ? { ...a, is_active: next } : a)));
      toast.success(next ? t("Publicité activée — visible publiquement") : t("Publicité désactivée — masquée du public"));
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    }
  };

  const period = (ad) =>
    ad.start_date || ad.end_date
      ? `${ad.start_date ? fmtDate(ad.start_date, { day: "2-digit", month: "short", year: "numeric" }) : "—"} → ${ad.end_date ? fmtDate(ad.end_date, { day: "2-digit", month: "short", year: "numeric" }) : "—"}`
      : t("En continu");

  const preview = ads?.find((a) => a.is_active) || ads?.[0];

  return (
    <div className="mt-4 grid items-start gap-5 lg:grid-cols-[1fr_330px]" data-testid="admin-ads">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <Megaphone className="h-4.5 w-4.5 text-[#2563EB]" size={18} />
              {t("Publicités")}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">{t("Gérez vos affiches publicitaires sur GoSpot.")}</p>
          </div>
          <button
            type="button"
            data-testid="ad-add-button"
            onClick={() => setEditing({ ...EMPTY })}
            className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8]"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("Ajouter une publicité")}
          </button>
        </div>

        <div className="mt-4 hidden grid-cols-[64px_1.4fr_1.2fr_1fr_90px_90px] gap-3 border-b border-slate-100 px-2 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 lg:grid">
          <span>{t("Image")}</span>
          <span>{t("Titre")}</span>
          <span>{t("Lien")}</span>
          <span>{t("Période")}</span>
          <span>{t("Statut")}</span>
          <span className="text-right">{t("Actions")}</span>
        </div>

        {ads === null ? (
          <div className="mt-4 h-40 animate-pulse rounded-2xl bg-slate-50" />
        ) : ads.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">{t("Aucune publicité pour le moment.")}</p>
        ) : (
          <div className="mt-1 divide-y divide-slate-50">
            {ads.map((ad) => (
              <div key={ad.id} data-testid={`admin-ad-${ad.id}`} className="grid items-center gap-3 px-2 py-3 lg:grid-cols-[64px_1.4fr_1.2fr_1fr_90px_90px]">
                {ad.image ? (
                  <img src={ad.image} alt={ad.title} className="h-10 w-16 rounded-lg object-cover" />
                ) : (
                  <span className="flex h-10 w-16 items-center justify-center rounded-lg bg-slate-100 text-slate-300">
                    <Megaphone className="h-4 w-4" />
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">{ad.title}</p>
                  {ad.slogan && <p className="truncate text-xs text-slate-400">{ad.slogan}</p>}
                </div>
                {ad.link ? (
                  <a href={ad.link} target="_blank" rel="noreferrer sponsored" className="truncate text-xs font-medium text-[#2563EB] hover:underline">
                    {ad.link}
                  </a>
                ) : (
                  <span className="text-xs text-slate-300">—</span>
                )}
                <span className="text-xs text-slate-500">{period(ad)}</span>
                <span className="flex flex-col items-start gap-1.5">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={ad.is_active}
                    aria-label={ad.is_active ? t("Désactiver la publicité") : t("Activer la publicité")}
                    data-testid={`admin-ad-toggle-${ad.id}`}
                    onClick={() => toggle(ad)}
                    className={`relative h-5 w-9 rounded-full transition ${ad.is_active ? "bg-emerald-500" : "bg-slate-300"}`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${ad.is_active ? "left-[18px]" : "left-0.5"}`}
                    />
                  </button>
                  <span
                    data-testid={`admin-ad-status-${ad.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold ${ad.is_active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${ad.is_active ? "bg-emerald-500" : "bg-slate-400"}`} />
                    {ad.is_active ? t("Active") : t("Inactive")}
                  </span>
                </span>
                <div className="flex justify-end gap-1.5">
                  <button
                    type="button"
                    data-testid={`admin-ad-edit-${ad.id}`}
                    onClick={() => setEditing({ ...ad, features: ad.features.join(", ") })}
                    aria-label={t("Modifier")}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-blue-300 hover:text-[#2563EB]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-ad-delete-${ad.id}`}
                    onClick={() => remove(ad)}
                    aria-label={t("Supprimer")}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-red-300 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FORMATS RECOMMANDÉS */}
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="ad-formats-card">
        <h2 className="text-sm font-bold text-slate-900">{t("Formats recommandés pour le poster")}</h2>
        <p className="mt-3 text-xs font-semibold text-slate-500">{t("Format desktop (accueil)")}</p>
        {preview?.image && (
          <div className="relative mt-2 overflow-hidden rounded-2xl">
            <img src={preview.image} alt={preview.title} className="h-24 w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 to-slate-950/20" />
            <div className="absolute inset-0 flex items-center justify-between px-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-extrabold uppercase text-white">{preview.title}</p>
                {preview.slogan && <p className="truncate text-[11px] font-semibold italic text-amber-300">{preview.slogan}</p>}
              </div>
              <span className="ml-2 flex shrink-0 items-center gap-1 rounded-full bg-amber-400 px-3 py-1.5 text-[10px] font-extrabold text-slate-900">
                {t("Découvrir")}
                <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </div>
        )}
        <ul className="mt-4 space-y-1.5 text-xs leading-relaxed text-slate-500">
          <li>{t("Taille recommandée : 1200 × 400 px (ratio 3:1)")}</li>
          <li>{t("Types : JPG, PNG")}</li>
          <li>{t("Poids max : 2 Mo")}</li>
        </ul>
      </div>

      {editing && (
        <AdForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};
